/**
 * AI チャットの発言 1 件
 *
 * 本文はプレーンテキストで描画する（HTML・Markdown・画像は解釈しない）。
 * リンクにするのは企画苔の URL だけ（utils/chatText.ts）。
 * 用途の結果には、表示データ（ChatResultBlocks）・コピー・操作ボタン・👍👎 が付く。
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import ChatActionCard from '@/components/chat/ChatActionCard';
import ChatResultBlocks from '@/components/chat/ChatResultBlocks';
import { CHAT_ENTRY_ROLE } from '@/constants/chat';
import { splitChatText } from '@/utils/chatText';
import type { ChatEntry, ChatFeedbackKind } from '@/services/chat';

interface ChatEntryItemProps {
  entry: ChatEntry;
  activeActionId: string | null;
  busy: boolean;
  onConfirm: (actionId: string, acknowledgeNotification: boolean) => void;
  onCancel: (actionId: string) => void;
  onNavigate: () => void;
  /** 結果の操作ボタン（日程の確定・共有メッセージに入れる など）。無ければ出さない */
  onEntryAction?: (kind: string, target: string | null) => void;
  onCopy?: (text: string) => void;
  onFeedback?: (kind: ChatFeedbackKind) => void;
}

function Feedback({ onFeedback }: { onFeedback: (kind: ChatFeedbackKind) => void }) {
  const [sent, setSent] = useState<ChatFeedbackKind | null>(null);
  const send = (kind: ChatFeedbackKind) => {
    if (sent) return;
    setSent(kind);
    onFeedback(kind);
  };
  return (
    <span className="ml-auto flex items-center gap-0.5 text-text-400">
      <button
        type="button"
        onClick={() => send('up')}
        aria-pressed={sent === 'up'}
        className={`rounded p-1 hover:text-primary-600 ${sent === 'up' ? 'text-primary-600' : ''}`}
        aria-label="役に立った"
      >
        <HandThumbUpIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => send('down')}
        aria-pressed={sent === 'down'}
        className={`rounded p-1 hover:text-danger-600 ${sent === 'down' ? 'text-danger-600' : ''}`}
        aria-label="役に立たなかった"
      >
        <HandThumbDownIcon className="h-4 w-4" />
      </button>
    </span>
  );
}

function ChatText({ text, onNavigate }: { text: string; onNavigate: () => void }) {
  return (
    <p className="whitespace-pre-wrap break-words">
      {splitChatText(text).map((segment, index) =>
        segment.type === 'link' ? (
          <Link
            key={index}
            to={segment.path}
            onClick={onNavigate}
            className="text-primary-700 underline underline-offset-2 break-all"
          >
            {segment.value}
          </Link>
        ) : (
          <span key={index}>{segment.value}</span>
        ),
      )}
    </p>
  );
}

export default function ChatEntryItem({
  entry,
  activeActionId,
  busy,
  onConfirm,
  onCancel,
  onNavigate,
  onEntryAction,
  onCopy,
  onFeedback,
}: ChatEntryItemProps) {
  if (entry.role === CHAT_ENTRY_ROLE.USER) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary-600 px-3.5 py-2 text-sm text-white">
          <p className="whitespace-pre-wrap break-words">{entry.text}</p>
        </div>
      </div>
    );
  }

  if (entry.role === CHAT_ENTRY_ROLE.NOTICE) {
    return (
      <div className="mx-auto max-w-[90%] rounded-lg bg-text-50 px-3 py-2 text-center text-xs text-text-600">
        <ChatText text={entry.text} onNavigate={onNavigate} />
      </div>
    );
  }

  const action = entry.action;
  const actions = entry.actions ?? [];
  const showTools = entry.copyable || actions.length > 0 || !!onFeedback;
  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[92%] text-sm text-text-800">
        <div className="rounded-2xl rounded-bl-md bg-text-50 px-3.5 py-2">
          <ChatText text={entry.text} onNavigate={onNavigate} />
          <ChatResultBlocks
            blocks={entry.blocks ?? []}
            busy={busy}
            onAction={onEntryAction ? (kind, target) => onEntryAction(kind, target) : undefined}
            onNavigate={onNavigate}
          />
        </div>
        {showTools && !action && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {entry.copyable && onCopy && (
              <button
                type="button"
                onClick={() => onCopy(entry.text)}
                className="flex items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700"
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                コピー
              </button>
            )}
            {onEntryAction &&
              actions.map((item) => (
                <button
                  key={`${item.kind}-${item.target ?? ''}`}
                  type="button"
                  disabled={busy}
                  onClick={() => onEntryAction(item.kind, item.target ?? null)}
                  className="rounded-lg border border-primary-400 px-2.5 py-1 text-xs text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                >
                  {item.label}
                </button>
              ))}
            {onFeedback && <Feedback onFeedback={onFeedback} />}
          </div>
        )}
        {action && (
          <ChatActionCard
            action={action}
            status={entry.actionStatus}
            isActive={action.actionId === activeActionId}
            busy={busy}
            onConfirm={(ack) => onConfirm(action.actionId, ack)}
            onCancel={() => onCancel(action.actionId)}
          />
        )}
      </div>
    </div>
  );
}
